import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PageMeta from "./components/PageMeta";

import Home from "./pages/Home";
import Approach from "./pages/Approach";
import Services from "./pages/Services";
import HowItWorks from "./pages/HowItWorks";
import Pricing from "./pages/Pricing";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import CaseLibrary from "./pages/CaseLibrary";
import ForLabs from "./pages/ForLabs";
import Enterprise from "./pages/Enterprise";
import Security from "./pages/Security";
import Privacy from "./pages/legal/Privacy";
import Cookies from "./pages/legal/Cookies";
import Terms from "./pages/legal/Terms";
import CookieBanner from "./components/CookieBanner";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

import PortalLayout from "./pages/portal/PortalLayout";
import PortalDashboard from "./pages/portal/Dashboard";
import NewCase from "./pages/portal/NewCase";
import PortalCaseDetail from "./pages/portal/CaseDetail";
import Statements from "./pages/portal/Statements";
import AuthCallback from "./pages/AuthCallback";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminCaseDetail from "./pages/admin/CaseDetail";
import AdminEnquiries from "./pages/admin/Enquiries";
import AdminPlanners from "./pages/admin/Planners";
import AdminBilling from "./pages/admin/Billing";

import PlannerLayout from "./pages/planner/PlannerLayout";
import PlannerDashboard from "./pages/planner/Dashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 1 });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
}

export function AppShell() {
  useLenis();
  const location = useLocation();
  // Google OAuth return: process #session_id synchronously before any route/guard runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <PageMeta />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/approach" element={<Approach />} />
        <Route path="/services" element={<Services />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/case-library" element={<CaseLibrary />} />
        <Route path="/for-labs" element={<ForLabs />} />
        <Route path="/enterprise" element={<Enterprise />} />
        <Route path="/security" element={<Security />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/portal" element={<ProtectedRoute role="client"><PortalLayout /></ProtectedRoute>}>
          <Route index element={<PortalDashboard />} />
          <Route path="new" element={<NewCase />} />
          <Route path="statements" element={<Statements />} />
          <Route path="cases/:id" element={<PortalCaseDetail />} />
        </Route>

        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="cases/:id" element={<AdminCaseDetail />} />
          <Route path="planners" element={<AdminPlanners />} />
          <Route path="billing" element={<AdminBilling />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
        </Route>

        <Route path="/planner" element={<ProtectedRoute role="planner"><PlannerLayout /></ProtectedRoute>}>
          <Route index element={<PlannerDashboard />} />
          <Route path="cases/:id" element={<AdminCaseDetail />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
        </AuthProvider>
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
